// Card #1798 — a single, pure classifier for "would the public board render
// real content for this mode/fields, or fall back to Blank". Two call sites
// share this rather than re-deriving the same `layoutFamilyForModeType`/
// `fields` presence check independently: ScoreboardContent.jsx (decides
// what to actually render) and ScoreboardPage.jsx (AC8 — derives an
// operator-facing status line from the SAME rule, via `onRenderStatus` +
// `postScoreboardStatus` over `boardStatusChannel`, without needing
// score-api to change at all). Keeping this in one place means the two
// never drift on what counts as "nothing to show."
//
// Deliberately pure and defensive: never throws on a missing/malformed
// `mode` (mirrors layoutFamilyForModeType's own fails-closed contract).
import { layoutFamilyForModeType, LAYOUT_FAMILY } from "./layoutFamilies";

export const BOARD_RENDER_REASON = {
  // Real content, or the Blank mode type configured on purpose — either way
  // the board is doing exactly what it was told to.
  OK: "ok",
  // `mode.modeType` doesn't resolve to any of the known layout families:
  // unset, unrecognized, or removed (#1798 AC5).
  UNKNOWN_MODE: "unknown-mode",
  // A recognized mode type, but score-api composed no `fields` for it right
  // now (no active class/ride/context resolved for this view, #1798 AC1 /
  // #1786's case).
  NO_ACTIVE_CLASS: "no-active-class",
  // MESSAGE resolved but its configured text is missing/whitespace-only
  // (#1795 AC6 / hard-requirement-3's "empty message routes to Blank").
  MESSAGE_EMPTY: "message-empty",
};

// Returns { blank, reason }. `blank` is true whenever ScoreboardContent
// falls back to BlankLayout instead of a real layout family.
export function classifyBoardRenderState({ mode, fields } = {}) {
  const modeType = mode?.modeType;
  const family = layoutFamilyForModeType(modeType);

  if (family === LAYOUT_FAMILY.BLANK) {
    return { blank: true, reason: BOARD_RENDER_REASON.OK };
  }
  // MESSAGE never flows its text through score-api's `fields` (it lives on
  // mode.message, resolved client-side like BLANK's logoAsset), so the
  // generic `!fields` check below is meaningless for it — `fields` is
  // always undefined here regardless of whether the message is set, and a
  // composed `{}` would be truthy anyway. Handled explicitly before that
  // check runs, mirroring the BLANK branch above.
  if (family === LAYOUT_FAMILY.MESSAGE) {
    const hasMessage =
      typeof mode?.message === "string" && mode.message.trim() !== "";
    return hasMessage
      ? { blank: false, reason: BOARD_RENDER_REASON.OK }
      : { blank: true, reason: BOARD_RENDER_REASON.MESSAGE_EMPTY };
  }
  if (!family) {
    return { blank: true, reason: BOARD_RENDER_REASON.UNKNOWN_MODE };
  }
  if (!fields) {
    return { blank: true, reason: BOARD_RENDER_REASON.NO_ACTIVE_CLASS };
  }
  return { blank: false, reason: BOARD_RENDER_REASON.OK };
}
